/**
 * Authentication utilities for the client application
 * 
 * Provides hooks and functions for user authentication with Azure Static Web Apps
 * and Microsoft Entra ID (Azure AD).
 */
import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode
} from 'react'

/**
 * User information from authentication
 */
export interface UserInfo {
  /** Unique identifier for the user */
  userId: string
  /** Display name (extracted from email) */
  userName: string
  /** Authentication provider (e.g., 'aad') */
  provider?: string
  /** User roles from Static Web Apps */
  roles?: string[]
  /** User principal name (preferred for domain checks) */
  upn: string
  /** Optional primary email claim if provided */
  email?: string
}

/**
 * Azure Static Web Apps client principal structure
 */
interface ClientPrincipal {
  userId: string
  userRoles: string[]
  claims: Array<{ typ: string; val: string }>
  identityProvider: string
  userDetails: string
}

/**
 * Response from /.auth/me endpoint
 */
interface AuthMeResponse {
  clientPrincipal: ClientPrincipal | null
}

/**
 * Authentication context state
 */
interface AuthContextType {
  /** Current authenticated user or null if not authenticated */
  user: UserInfo | null
  /** Whether authentication state is still loading */
  isLoading: boolean
  /** Error message if authentication failed */
  error: string | null
  /** Function to trigger login */
  login: () => void
  /** Function to trigger logout */
  logout: () => void
  /** Function to refresh authentication state */
  refresh: () => Promise<void>
}

/**
 * Fetches the current authenticated user from the /.auth/me endpoint
 * 
 * @returns User information or null if not authenticated
 */
export const fetchUser = async (): Promise<UserInfo | null> => {
  // Detect local‐development environment (SWA emulator / plain `npm start`)
  // Prefixed with underscore to indicate intentional (currently unused) variables
  const _isLocalhost = /^localhost$|^127(\.\d+){3}$/.test(window.location.hostname)
  const _MOCK_USER: UserInfo = {
    userId: 'local-dev',
    userName: 'dev',
    email: 'dev@thedevelopmentfactory.com',
    upn: 'dev@thedevelopmentfactory.com',
    provider: 'mock',
    roles: ['authenticated']
  }

  try {
    const res = await fetch('/.auth/me')

    if (!res.ok) {
      // In dev simply treat 401/403 as “not logged-in”
      throw new Error(`Authentication error: ${res.status} ${res.statusText}`)
    }

    /* ------------------------------------------------------------------
       Some hosting tiers return text/html (an error page) instead of JSON
       which causes a SyntaxError. Safeguard by checking the content-type.
    ------------------------------------------------------------------ */
    const ct = res.headers.get('content-type') ?? ''
    let data: AuthMeResponse
    if (ct.includes('application/json')) {
      data = (await res.json()) as AuthMeResponse
    } else {
      const txt = await res.text()
      if (txt.trim().startsWith('<')) {
        // HTML → probably EasyAuth error page (unauthenticated)
        throw new Error('Received HTML instead of JSON from /.auth/me')
      }
      data = JSON.parse(txt) as AuthMeResponse
    }

    const principal = data.clientPrincipal

    if (!principal) {
      return null
    }

    // -------------------- claim helpers --------------------
    const findClaim = (typeEndsWith: string): string | undefined =>
      principal.claims?.find(c => c.typ.toLowerCase().endsWith(typeEndsWith))?.val

    /* --------------------------------------------------------
       From June-2025 config we set `userDetailsClaim` to the
       Azure AD “preferred_username” (UPN). Therefore:
       1.  primary source → principal.userDetails
       2.  fallback       → explicit /upn claim (older config)
    --------------------------------------------------------- */
    const upn = principal.userDetails || findClaim('/upn') || ''
    // Email claim (may be absent for some AAD B2B / guest accounts)
    const emailClaim = findClaim('/email')

    // Validate that the user is from an allowed domain
    if (!isAllowedDomain(upn)) {
      console.warn('User not from allowed domain:', upn)
      return null
    }

    const userName = upn.includes('@') ? upn.split('@')[0] : upn

    return {
      userId: principal.userId,
      userName,
      upn,
      email: emailClaim,
      provider: principal.identityProvider,
      roles: principal.userRoles
    }
  } catch (error) {
    console.warn('Failed to fetch user (treated as unauthenticated):', error)
    return null
  }
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  error: null,
  login: () => { },
  logout: () => { },
  refresh: async () => { }
})

/**
 * Authentication provider component
 * 
 * Wraps the application and provides authentication state and functions
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const userData = await fetchUser()
      setUser(userData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  const login = () => {
    // Always invoke the Azure Static Web Apps authentication endpoint.
    // Local-development handling is managed centrally in App.tsx; here we
    // consistently target the standard AAD login route.
    window.location.href = '/.auth/login/aad'
  }

  const logout = () => {
    // Redirect to logout endpoint
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/'
  }
  // JSX is not allowed in *.ts files; use React.createElement instead
  return React.createElement(
    AuthContext.Provider,
    { value: { user, isLoading, error, login, logout, refresh } },
    children
  )
}

/**
 * Hook to use authentication in components
 * 
 * @returns Authentication state and functions
 */
export const useAuth = (): AuthContextType => {
  return useContext(AuthContext)
}

/**
 * Checks if a user is authenticated and from an allowed domain
 * 
 * @param upn User principal name (or email) used for sign-in
 * @returns True if the identifier ends with an allowed domain
 */
export const isAllowedDomain = (upn = ''): boolean => {
  if (!upn) return false

  const id = upn.toLowerCase()
  return id.endsWith('@thedevelopmentfactory.com') ||
    id.endsWith('@courthousenews.com')
}

/**
 * Formats a user's display name from their email
 * 
 * @param email User's email address
 * @returns Formatted display name
 */
export const formatUserName = (email = ''): string => {
  if (!email || !email.includes('@')) return email

  const name = email.split('@')[0]
  // Convert name.with.dots or name_with_underscores to proper case
  return name
    .replace(/[._]/g, ' ')
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
}
